/**
 * Pure period logic for the sales dashboard (VIB-1093, F0.2).
 *
 * A DashboardPeriod is a logical spec (granularity + year + month/quarter, or
 * custom Oslo-local date bounds) — never a pair of UTC instants. Membership of
 * a UTC timestamp in a period is decided by projecting the instant into the
 * Oslo (Europe/Oslo) calendar via osloDateString(), so bucketing is DST-aware
 * and host-timezone independent (jf. funn TOPP-3 / oslo-time.ts).
 *
 * No Supabase/React imports — this is the test oracle that PeriodContext wraps.
 * `now` is always passed in so every function is deterministic and testable.
 */
import { osloDateString, osloDateParts } from "./oslo-time";

export type PeriodGranularity = "month" | "quarter" | "year" | "custom";

export interface DashboardPeriod {
  granularity: PeriodGranularity;
  /** Calendar year (Oslo). For custom, the year of `customFrom`. */
  year: number;
  /** 1-12, only for granularity "month". */
  month?: number;
  /** 1-4, only for granularity "quarter". */
  quarter?: number;
  /** Oslo-local YYYY-MM-DD, inclusive start. Only for granularity "custom". */
  customFrom?: string;
  /** Oslo-local YYYY-MM-DD, inclusive end. Only for granularity "custom". */
  customTo?: string;
}

const MONTH_NAMES_NB = [
  "januar", "februar", "mars", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "desember",
];

/** Last calendar day (1-31) of a 1-indexed Oslo month. Pure calendar math. */
function lastDayOfMonth(year: number, month: number): number {
  // Date.UTC(year, month, 0) = day 0 of the next 0-indexed month = last day
  // of `month`. Used only for day-count arithmetic, never timezone shifting.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Inclusive day-count between two YYYY-MM-DD strings (a <= b assumed). */
function daysInclusive(fromDate: string, toDate: string): number {
  const a = Date.parse(`${fromDate}T00:00:00Z`);
  const b = Date.parse(`${toDate}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

/**
 * Oslo-local inclusive bounds of a period as YYYY-MM-DD strings.
 * String comparison of these is chronological (lexicographic == temporal).
 */
export function periodBounds(period: DashboardPeriod): {
  fromDate: string;
  toDate: string;
} {
  switch (period.granularity) {
    case "month": {
      const m = period.month ?? 1;
      return {
        fromDate: `${period.year}-${pad(m)}-01`,
        toDate: `${period.year}-${pad(m)}-${pad(lastDayOfMonth(period.year, m))}`,
      };
    }
    case "quarter": {
      const q = period.quarter ?? 1;
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      return {
        fromDate: `${period.year}-${pad(startMonth)}-01`,
        toDate: `${period.year}-${pad(endMonth)}-${pad(lastDayOfMonth(period.year, endMonth))}`,
      };
    }
    case "year":
      return { fromDate: `${period.year}-01-01`, toDate: `${period.year}-12-31` };
    case "custom":
      return {
        fromDate: period.customFrom ?? `${period.year}-01-01`,
        toDate: period.customTo ?? `${period.year}-12-31`,
      };
  }
}

/** True if a UTC instant falls within the period, evaluated in Oslo time. */
export function periodContains(
  period: DashboardPeriod,
  utcInstant: string | number | Date,
): boolean {
  const { fromDate, toDate } = periodBounds(period);
  const d = osloDateString(utcInstant);
  return d >= fromDate && d <= toDate;
}

/**
 * Whole weeks remaining from `now` to the end of the period (inclusive of the
 * current day). Returns 0 once the period has ended; floors at 1 while the
 * period is still running so BF-gap "behov per uke" never divides by zero.
 */
export function remainingWeeks(
  period: DashboardPeriod,
  now: string | number | Date,
): number {
  const { fromDate, toDate } = periodBounds(period);
  const nowDate = osloDateString(now);
  if (nowDate > toDate) return 0;
  const startDate = nowDate < fromDate ? fromDate : nowDate;
  const days = daysInclusive(startDate, toDate);
  return Math.max(1, Math.ceil(days / 7));
}

/** The period covering `now` at the given granularity (Oslo calendar). */
export function periodForNow(
  granularity: PeriodGranularity,
  now: string | number | Date,
): DashboardPeriod {
  const { year, month } = osloDateParts(now);
  switch (granularity) {
    case "month":
      return { granularity, year, month };
    case "quarter":
      return { granularity, year, quarter: Math.floor((month - 1) / 3) + 1 };
    case "year":
      return { granularity, year };
    case "custom": {
      const ds = osloDateString(now);
      return { granularity, year, customFrom: ds, customTo: ds };
    }
  }
}

/** Default period: the current quarter (Oslo). */
export function defaultPeriod(now: string | number | Date): DashboardPeriod {
  return periodForNow("quarter", now);
}

/**
 * Shift a period by `delta` units of its own granularity (e.g. quarter ±1),
 * rolling year boundaries. Custom periods are returned unchanged (no natural
 * unit to step). Used by the period selector's prev/next arrows.
 */
export function shiftPeriod(period: DashboardPeriod, delta: number): DashboardPeriod {
  switch (period.granularity) {
    case "month": {
      const zeroIdx = (period.month ?? 1) - 1 + delta;
      const year = period.year + Math.floor(zeroIdx / 12);
      const month = ((zeroIdx % 12) + 12) % 12 + 1;
      return { granularity: "month", year, month };
    }
    case "quarter": {
      const zeroIdx = (period.quarter ?? 1) - 1 + delta;
      const year = period.year + Math.floor(zeroIdx / 4);
      const quarter = ((zeroIdx % 4) + 4) % 4 + 1;
      return { granularity: "quarter", year, quarter };
    }
    case "year":
      return { granularity: "year", year: period.year + delta };
    case "custom":
      return period;
  }
}

/** Human-readable Norwegian label for the period selector. */
export function periodLabel(period: DashboardPeriod): string {
  switch (period.granularity) {
    case "month":
      return `${MONTH_NAMES_NB[(period.month ?? 1) - 1]} ${period.year}`;
    case "quarter":
      return `Q${period.quarter ?? 1} ${period.year}`;
    case "year":
      return `${period.year}`;
    case "custom": {
      const { fromDate, toDate } = periodBounds(period);
      return `${fromDate} – ${toDate}`;
    }
  }
}
